/* @flow */
import cx from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import BoxButton from 'components/BoxButton';
import Button from 'components/Button';
import Container from 'components/Container';
import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Image from 'components/Image';
import { storeLinkFor } from 'lib/MobileAppIntegration';
import MediaObject from 'components/MediaObject';
import openApp from 'utils/openApp';
import Row from 'components/Row';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import { withUserAgent, UserAgent } from 'lib/currentUserAgent';
import View from 'components/View';
import { origins } from 'constants/SetupConstants';
import LinkConstants from 'constants/LinkConstants';
import JoinFlowGroupStorage from 'join/data/JoinFlowGroupStorage';

import './styles/AppUpsellPage.scss';

import type { GlyphName } from '@remind/r101-glyphs';
import type { SetupOrigin } from 'constants/SetupConstants';

type Props = {
  groupName?: ?string,
  origin: SetupOrigin,
  haveAppUrl: ?string,
  userAgent: UserAgent,
};

const AppUpsellPageRatingIcon = <Glyph name="star" color="sunrise" size="xxsmall" />;

function AppUpsellPageBox(props: {
  buttonColor?: string,
  buttonDisabled?: boolean,
  buttonText: string,
  children?: React.Node,
  icon?: GlyphName,
  image?: string,
  text?: string,
  theme: 'admin' | 'brand',
  title: string,
  url?: string,
}) {
  const {
    buttonColor = 'brand',
    buttonDisabled = false,
    buttonText,
    children,
    icon,
    image,
    text,
    theme,
    title,
    url = '#',
  } = props;

  return (
    <Box textAlign="left" className="AppUpsellPage-box">
      <BoxBody size="small">
        <MediaObject gutter="small">
          <div className={cx('AppUpsellPage-box-icon', `AppUpsellPage-box-icon--${theme}`)}>
            {icon && <Glyph name={icon} color="white" size="xxlarge" />}
            {image && <Image src={image} />}
          </div>
          <div>
            <Heading flush level={'2'} levelStyle={'5'}>
              {title}
            </Heading>
            {children}
            {text && (
              <Text color="eclipse" element="div" size="xxxsmall">
                {text}
              </Text>
            )}
          </div>
        </MediaObject>
      </BoxBody>
      <BoxButton href={url} color={buttonColor} disabled={buttonDisabled}>
        {buttonText}
      </BoxButton>
    </Box>
  );
}

function titleFor(origin: SetupOrigin, groupName: ?string) {
  if (groupName) {
    if (origin === origins.CREATED_GROUP) {
      return `${groupName} is ready!`;
    } else if (origin === origins.SUBSCRIBED) {
      return `Go to ${groupName}!`;
    }
  }

  return 'Get the Remind app';
}

class AppUpsellPage extends React.Component<Props> {
  // eslint-disable-next-line @remind/remind/legacy-context
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  // TODO make the server redirect with HTTP and still send back the HTML.
  // As is, we're making user wait for a megabyte app to download before
  // redirecting client-side, all to show this small HTML
  componentDidMount() {
    const { query } = this.context.router.location;
    const { userAgent } = this.props;

    if (query['store_redirect'] === '1' && userAgent.ios()) {
      // Redirection to the App Store for iOS users is done here instead of r101-mobile-provider
      // so that if they cancel it, they are here instead of a blank page.
      //
      // Android does not have this problem.
      window.location = LinkConstants.APP_STORE;
    }
  }

  // TODO make this a route so we don't have to load the app in order to branch on it
  alreadyHaveApplication = () => {
    openApp(this.props.haveAppUrl);
  };

  render() {
    const { origin, groupName, haveAppUrl, userAgent } = this.props;
    // join class name gets set during join flows so we can display class name here!
    const joinClassName = JoinFlowGroupStorage.getJoinedClassName();
    const titleComp = joinClassName ? (
      <TitleHeader
        title="Great!"
        subtext={`Your account has been created and you are in ${joinClassName}.`}>
        Next Step: get the app.
      </TitleHeader>
    ) : (
      <TitleHeader
        title={titleFor(origin, groupName)}
        subtext="Send and receive messages instantly on your phone."
      />
    );

    return (
      <View className="AppUpsellPage">
        <div className="AppUpsellPage-inner">
          <Container size="xsmall">
            {titleComp}
            <Row>
              <AppUpsellPageBox
                buttonText="Download Remind app"
                icon="remind-icon"
                text="30,000+ 5-star reviews"
                theme="brand"
                title="Remind"
                url={storeLinkFor(userAgent)}>
                <div className="AppUpsellPage-box-ratingIcons">
                  {AppUpsellPageRatingIcon}
                  {AppUpsellPageRatingIcon}
                  {AppUpsellPageRatingIcon}
                  {AppUpsellPageRatingIcon}
                  {AppUpsellPageRatingIcon}
                </div>
              </AppUpsellPageBox>
            </Row>
            {haveAppUrl &&
              origin === origins.DIRECT && (
                <Button
                  color="brand"
                  full
                  level="text"
                  weight="semibold"
                  eventElementName="openAppButton"
                  onClick={this.alreadyHaveApplication}>
                  I already have the app
                </Button>
              )}
          </Container>
        </div>
      </View>
    );
  }
}

export { AppUpsellPage };
export default withUserAgent<Props>(AppUpsellPage);
